import React, { forwardRef, useState, useRef, useEffect } from "react";
import { CaretDown, CircleNotch } from "@phosphor-icons/react";
import { tv, type VariantProps } from "tailwind-variants";

const bottomBorderStyle = tv({
  base: [
    "flex items-center justify-between",
    "border-b",
    "py-1",
    "transition-all duration-300",
    "relative",
  ],
  variants: {
    status: {
      default: "border-gray-300",
      focus: "border-blue-500",
      disabled: "border-gray-200 opacity-50 cursor-not-allowed",
      error: "border-red-500",
    },
    size: {
      sm: "text-xs",
      md: "text-sm",
      lg: "text-base",
    },
  },
  defaultVariants: {
    status: "default",
    size: "md",
  },
});

type Option<T = unknown> = string | { label: string; value: T };

type BottomBorderControlProps<T = unknown> = Partial<
  VariantProps<typeof bottomBorderStyle>
> & {
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  searchable?: boolean;
  options?: Option[];
  onSelect?: (val: T) => void;
  onChangeText?: (text: string) => void;
  width?: string | number;
};

const InputBottomBorderInner = <T,>(
  {
    value,
    placeholder = "Chọn",
    disabled = false,
    loading = false,
    options,
    onSelect,
    onChangeText,
    width = "100%",
    status,
    size,
    searchable,
  }: BottomBorderControlProps<T>,
  ref: React.ForwardedRef<HTMLInputElement>
) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState(value || "");

  useEffect(() => {
    setSearchTerm(value || "");
  }, [value]);

  const filteredOptions = options?.filter((opt) => {
    if (!searchable || !searchTerm) return true;
    const label = typeof opt === "string" ? opt : opt.label;
    return label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setSearchTerm(text);
    onChangeText?.(text);
    if (!isOpen) setIsOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative" style={{ width }}>
      <div
        className={bottomBorderStyle({
          status: disabled ? "disabled" : isOpen ? "focus" : status,
          size,
        })}
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
      >
        <input
          ref={ref}
          value={searchTerm}
          onChange={handleInputChange}
          placeholder={placeholder}
          readOnly={!searchable}
          disabled={disabled}
          className={`w-full text-[14px] font-medium bg-transparent outline-none ${!disabled && "cursor-pointer"} placeholder:text-gray-400`}
        />

        <div className="ml-2 flex items-center text-gray-400 cursor-pointer">
          {loading ? (
            <CircleNotch size={16} className="animate-spin text-blue-500" />
          ) : (
            <CaretDown
              size={16}
              className={`transition-transform duration-300 ${isOpen ? "rotate-180 text-blue-500" : ""}`}
            />
          )}
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-[999] w-full bg-white border border-gray-100 rounded-lg shadow-xl mt-1 py-1 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          <ul className="max-h-60 overflow-y-auto custom-scrollbar">
            {filteredOptions && filteredOptions.length > 0 ? (
              filteredOptions.map((opt, index) => {
                const label = typeof opt === "string" ? opt : opt.label;
                const val = (typeof opt === "string" ? opt : opt.value) as T;
                const isSelected =
                  searchTerm.toLowerCase() === label.toLowerCase();

                return (
                  <li
                    key={index}
                    className={`px-4 py-2 text-sm cursor-pointer transition-colors
                      ${isSelected ? "text-blue-600 bg-blue-50 font-medium" : "text-gray-700 hover:bg-gray-50"}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect?.(val);
                      setIsOpen(false);
                    }}
                  >
                    {label}
                  </li>
                );
              })
            ) : (
              <li className="px-4 py-3 text-center text-gray-400 text-sm">
                Không tim thấy kết quả
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export const InputBottomBorder = forwardRef(InputBottomBorderInner) as <T>(
  props: BottomBorderControlProps<T> & {
    ref?: React.ForwardedRef<HTMLInputElement>;
  }
) => React.ReactElement;

export default InputBottomBorder;
